import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle, Trash, Funnel, ArrowsClockwise } from "@phosphor-icons/react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/app/ui-bits";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const KIND_LABEL = {
  approval: "Approval", compliance: "Compliance", deliverable: "Deliverable",
  payroll: "Payroll", asset: "Asset", appointment: "Appointment",
  task: "Task", finance: "Finance", system: "System",
};
const KIND_TONE = {
  approval: "bg-blue-100 text-blue-700",
  compliance: "bg-amber-100 text-amber-700",
  deliverable: "bg-blue-100 text-blue-700",
  payroll: "bg-emerald-100 text-emerald-700",
  asset: "bg-amber-100 text-amber-700",
  appointment: "bg-emerald-100 text-emerald-700",
  task: "bg-slate-100 text-slate-700",
  finance: "bg-blue-100 text-blue-700",
  system: "bg-slate-100 text-slate-600",
};
const SEV_DOT = {
  critical: "bg-red-500", warning: "bg-amber-500",
  success: "bg-emerald-500", info: "bg-blue-500",
};

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setBusy(true);
    try {
      const params = { limit: 200 };
      if (filter === "unread") params.only_unread = true;
      if (kindFilter !== "all") params.kind = kindFilter;
      const { data } = await api.get("/notifications", { params });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter, kindFilter]);

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/notifications/sync-system-signals");
      toast.success(`Synced · ${data.created} new`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      toast.success("All marked read");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onItemClick = async (n) => {
    if (!n.read_at) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    if (n.link) {
      const link = n.link.startsWith("/admin") ? n.link.slice(6) : n.link;
      navigate(link || "/");
    } else { load(); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      setItems((rows) => rows.filter((r) => r.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="notifications-page">
      <PageHeader
        eyebrow="System"
        title="Notifications"
        description={`${unread} unread · live signals from approvals, compliance, deliverables, payroll, assets and donor reports.`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={sync} disabled={busy} data-testid="notif-sync-btn">
              <ArrowsClockwise size={14} className={busy ? "animate-spin mr-1" : "mr-1"} /> Sync signals
            </Button>
            {unread > 0 && (
              <Button onClick={markAllRead} className="btn-primary-blue" data-testid="notif-mark-all-read">
                <CheckCircle size={14} className="mr-1" /> Mark all read
              </Button>
            )}
          </div>
        }
      />

      <div className="space-y-5 p-5 md:p-8">
        <div className="dc-card p-4 flex flex-wrap items-center gap-3">
          <Funnel size={14} className="text-slate-400" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-40" data-testid="notif-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread only</SelectItem>
            </SelectContent>
          </Select>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="h-8 w-44" data-testid="notif-kind-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              {Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="ml-auto font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{items.length} shown</span>
        </div>

        <div className="dc-card overflow-hidden">
          {items.length === 0 ? (
            <EmptyState icon={Bell} title="All caught up" description="No notifications match your filters." />
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="notif-list">
              {items.map((n) => (
                <li key={n.id}>
                  <div
                    onClick={() => onItemClick(n)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter") onItemClick(n); }}
                    className={`group flex w-full cursor-pointer gap-4 px-5 py-4 text-left transition hover:bg-slate-50 ${!n.read_at ? "bg-blue-50/30" : ""}`}
                    data-testid={`notif-row-${n.id}`}
                  >
                    <span className={`mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full ${SEV_DOT[n.severity] || SEV_DOT.info}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`font-mono-alt rounded px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${KIND_TONE[n.kind] || KIND_TONE.system}`}>
                          {KIND_LABEL[n.kind] || n.kind}
                        </span>
                        {!n.read_at && <span className="font-mono-alt rounded bg-blue-100 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-blue-700">NEW</span>}
                        <span className="ml-auto font-mono-alt text-[10px] text-slate-400">{formatDate(n.created_at)}</span>
                      </div>
                      <div className="mt-1 truncate text-sm font-semibold text-slate-900">{n.title}</div>
                      {n.body && <div className="mt-0.5 text-xs text-slate-500">{n.body}</div>}
                    </div>
                    <button
                      onClick={(e) => remove(e, n.id)}
                      className="opacity-0 group-hover:opacity-100"
                      aria-label="Delete"
                      data-testid={`notif-delete-${n.id}`}
                    >
                      <Trash size={14} className="text-slate-400 hover:text-red-500" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
